import React from 'react';
import PropTypes from 'prop-types';

/**
 * ErrorBoundary Component
 * 
 * Catches JavaScript errors anywhere in the child component tree,
 * logs errors, and displays a fallback UI instead of crashing the app.
 * 
 * Usage: Wrap your app or specific components
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so next render shows fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // You can also log to an error reporting service here
    // e.g., Sentry.captureException(error);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div 
          className="d-flex flex-column justify-content-center align-items-center min-vh-100 bg-light p-4"
          style={{ textAlign: 'center' }}
        >
          <div className="card shadow-lg p-5" style={{ maxWidth: '600px' }}>
            <div className="card-body">
              <h1 className="display-4 text-danger mb-4">
                Oops! Something went wrong
              </h1>
              <p className="lead text-muted mb-4">
                We're sorry for the inconvenience. An unexpected error occurred.
              </p>
              
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="text-start mb-4" style={{ whiteSpace: 'pre-wrap' }}>
                  <summary className="text-danger cursor-pointer mb-2">
                    <strong>Error Details (Development Only)</strong>
                  </summary>
                  <div className="alert alert-danger">
                    <p><strong>{this.state.error.toString()}</strong></p>
                    {this.state.errorInfo && (
                      <pre className="small" style={{ fontSize: '0.75rem' }}>
                        {this.state.errorInfo.componentStack}
                      </pre>
                    )}
                  </div>
                </details>
              )}

              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <button
                  onClick={this.handleReload}
                  className="btn btn-primary btn-lg"
                >
                  Reload Page
                </button>
                <button
                  onClick={this.handleGoHome}
                  className="btn btn-outline-secondary btn-lg"
                >
                  Go to Homepage
                </button>
              </div>

              <p className="text-muted mt-4 small">
                If this problem persists, please contact support.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired
};

export default ErrorBoundary;
